/** @jsxRuntime classic */
/** @jsx jsx */
import { css, keyframes, jsx } from '@emotion/core';
import { ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { getErrorInfo } from 'src/utils/errors';
import { zIndex } from 'src/utils/zIndex';

export type GithubStepKey = 'createWebsite' | 'connectRepo' | 'installApp' | 'triggerDeploy';

export type GithubStepStatus = 'pending' | 'running' | 'done' | 'error';

export interface GithubStep {
  key: GithubStepKey;
  label: ReactNode;
  status: GithubStepStatus;
  error?: string;
}

interface Props {
  steps?: GithubStep[];
}

// Benevolent deception: a call that settles almost instantly would flash
// its "running" line away before it can be read, making the log jitter.
// Hold every step in the running state for at least this long.
const GITHUB_STEP_MIN_VISIBLE_MS = 500;

// How long a failed step stays on screen before the caller's shared
// error handling (toast + form restore) takes over again.
const GITHUB_STEP_ERROR_DWELL_MS = 1800;

// how long the previous step takes to scroll out of view (and the next
// one to scroll in) inside the one-row terminal viewport
const STEP_SCROLL_MS = 400;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface GithubDeployStepsInit {
  withCreateWebsite: boolean;
  domain?: string;
  repoFullName: string;
  withApp: boolean;
  /** include the "Deploying the site from GitHub" step, i.e. start an initial deployment */
  withAutoDeploy: boolean;
}

/**
 * Tracks the real API calls of a github deploy flow (create website,
 * connect repository, optionally create the persistent app, trigger the
 * first deployment) so the AddWebsiteGithubProgress overlay can report
 * which call is in flight and its actual outcome. Shared by the
 * add-website github flow and the projects DeployToWebsite modal.
 */
export const useGithubDeploySteps = () => {
  const [steps, setSteps] = useState<GithubStep[] | undefined>(undefined);

  const initSteps = (opts: GithubDeployStepsInit) => {
    const built: GithubStep[] = [];

    if (opts.withCreateWebsite) {
      built.push({
        key: 'createWebsite',
        status: 'pending',
        label: (
          <FormattedMessage
            id="websites.add_website.github_progress.create_website"
            defaultMessage="Creating website {domain}"
            values={{ domain: opts.domain ?? '' }}
          />
        ),
      });
    }

    built.push({
      key: 'connectRepo',
      status: 'pending',
      label: (
        <FormattedMessage
          id="websites.add_website.github_progress.connect_repo"
          defaultMessage="Connecting GitHub repository {repo}"
          values={{ repo: opts.repoFullName }}
        />
      ),
    });

    if (opts.withApp) {
      built.push({
        key: 'installApp',
        status: 'pending',
        label: (
          <FormattedMessage
            id="websites.add_website.github_progress.install_app"
            defaultMessage="Creating worker process"
          />
        ),
      });
    }

    if (opts.withAutoDeploy) {
      built.push({
        key: 'triggerDeploy',
        status: 'pending',
        label: (
          <FormattedMessage
            id="websites.add_website.github_progress.trigger_deploy"
            defaultMessage="Deploying the site from GitHub"
          />
        ),
      });
    }

    setSteps(built);
  };

  const patchStep = (key: GithubStepKey, patch: Partial<GithubStep>) => {
    setSteps((prev) => prev?.map((step) => (step.key === key ? { ...step, ...patch } : step)));
  };

  // run one API call of the flow, reporting its real outcome to the
  // overlay before rethrowing to the caller's shared error handling
  const runStep = useCallback(async <T,>(key: GithubStepKey, fn: () => Promise<T>): Promise<T> => {
    const startedAt = Date.now();
    patchStep(key, { status: 'running', error: undefined });

    try {
      const result = await fn();
      // keep the running line visible long enough to be read, even for
      // calls that settle almost instantly
      const elapsed = Date.now() - startedAt;

      if (elapsed < GITHUB_STEP_MIN_VISIBLE_MS) {
        await delay(GITHUB_STEP_MIN_VISIBLE_MS - elapsed);
      }

      patchStep(key, { status: 'done' });
      return result;
    } catch (e) {
      const { message } = getErrorInfo(e);
      patchStep(key, { status: 'error', error: message });
      // hold the overlay briefly so the failed step stays readable
      // before the shared error handling restores the form
      await delay(GITHUB_STEP_ERROR_DWELL_MS);
      throw e;
    }
  }, []);

  return { steps, initSteps, runStep };
};

const dotBlink = keyframes`
  0%, 20% {
    opacity: 0;
   }

   40%, 100% {
    opacity: 1;
   }
`;

const stepPrefix = (status: GithubStepStatus) => css`
  flex-shrink: 0;
  width: 1.2em;
  color: ${status === 'error' ? '#f87171' : '#34d399'};
`;

const stepRow = (status: GithubStepStatus) => css`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  min-height: 1.4em;
  line-height: 1.4em;
  opacity: ${status === 'pending' ? 0.4 : 1};
  color: ${status === 'error' ? '#f87171' : '#d1fae5'};
`;

const dot = (delayMs: number) => css`
  opacity: 0;
  animation: ${dotBlink} 1.2s infinite;
  animation-delay: ${delayMs}ms;
`;

const styles = {
  terminal: css`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    width: 100%;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    background: #111827;
    color: #d1fae5;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.9rem;
  `,
  label: css`
    flex: 0 1 auto;
  `,
  error: css`
    flex: 1 1 auto;
    min-width: 0;
    white-space: pre-line;
    word-break: break-word;
  `,
  dots: css`
    display: inline-flex;
    width: 1.5em;
    margin-left: 0.1em;
  `,
  stepViewport: css`
    overflow: hidden;
    min-height: 1.4em;
    transition: height ${STEP_SCROLL_MS}ms ease;
  `,
  stepTrack: css`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    transition: transform ${STEP_SCROLL_MS}ms ease;
    will-change: transform;
  `,
};

/**
 * The overlay shown while a github-deploy website is being created: the
 * same shell as AddWebsiteAnimation's app-install overlay (icon above a
 * terminal-style step log), but driven by the real API calls of the
 * creation flow, so each line reports the actual call in flight and its
 * outcome instead of timer-cycled placeholder messages. Only the current
 * step is on show: as the flow advances the previous step scrolls away
 * vertically and the next one scrolls in.
 */
export const AddWebsiteGithubProgress = ({ steps }: Props) => {
  const stepList = useMemo(() => steps ?? [], [steps]);
  const trackRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState({ offset: 0, height: 0 });

  // the row on show: the one running (or failed); while the next call has
  // not started yet — and once the flow has settled — pin the last
  // finished row, so the terminal is never left empty
  const running = stepList.findIndex((step) => step.status === 'running' || step.status === 'error');
  const activeIndex =
    running === -1 ? stepList.reduce((last, step, index) => (step.status === 'done' ? index : last), 0) : running;

  useLayoutEffect(() => {
    const track = trackRef.current;
    const row = track?.children[activeIndex] as HTMLElement | undefined;
    const firstRow = track?.children[0] as HTMLElement | undefined;

    if (!row || !firstRow) {
      return;
    }

    // offsetTop is relative to the fixed overlay, so it is normalised by
    // the first row; the height is measured per row so a multi-line error
    // row is not clipped by the viewport
    setViewport({ offset: row.offsetTop - firstRow.offsetTop, height: row.offsetHeight });
  }, [stepList, activeIndex]);

  return (
    <div data-qa="addWebsiteGithubProgress">
      <Box
        d="column"
        j="center"
        a="center"
        position="fixed"
        top={'0'}
        left={'0'}
        w="100vw"
        h="100vh"
        zIndex={zIndex.WebsiteAnimation}
        bg="grey5"
      >
        <Box w={{ xs: 10, sm: 10, md: 6 }} h="100%" a="center" j="center" zIndex={zIndex.WebsiteAnimation}>
          <Box d="column" a="center" zIndex={zIndex.WebsiteAnimation} w="100%">
            <Icon name="github" size="3xl" marginBottom="xl" />
            <div css={styles.terminal}>
              <div css={styles.stepViewport} style={viewport.height ? { height: `${viewport.height}px` } : undefined}>
                <div ref={trackRef} css={styles.stepTrack} style={{ transform: `translateY(-${viewport.offset}px)` }}>
                  {stepList.map((step) => (
                    <div key={step.key} css={stepRow(step.status)}>
                      <span css={stepPrefix(step.status)} aria-hidden="true">
                        {step.status === 'done' ? '✓' : step.status === 'error' ? '✗' : '$'}
                      </span>
                      <span css={styles.label}>{step.label}</span>
                      {step.status === 'running' && (
                        <span css={styles.dots} aria-hidden="true">
                          <span css={dot(0)}>.</span>
                          <span css={dot(200)}>.</span>
                          <span css={dot(400)}>.</span>
                        </span>
                      )}
                      {step.status === 'error' && step.error && <span css={styles.error}>- {step.error}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Box>
        </Box>
      </Box>
    </div>
  );
};
